// Lightweight i18n for the GuardAI security-patrol dashboard.
// - Flat dotted-key dictionaries, one file per language.
// - English is the master; other locales fall back to English, then to the
//   key itself (or an inline default passed to t()).
// - Selection persists to localStorage and drives document `lang`/`dir`
//   (Arabic is RTL).
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import en, { type TranslationKey } from "./locales/en";
import es from "./locales/es";
import fr from "./locales/fr";
import zh from "./locales/zh";
import ar from "./locales/ar";
import hi from "./locales/hi";
import pt from "./locales/pt";
import sw from "./locales/sw";

export type LangCode = "en" | "es" | "fr" | "zh" | "ar" | "hi" | "pt" | "sw";
type Dict = Partial<Record<TranslationKey, string>>;

export interface LanguageMeta {
  code: LangCode;
  name: string; // English name
  nativeName: string; // Endonym
  flag: string; // Emoji flag
  dir: "ltr" | "rtl";
}

// One language per inhabited continent (English default).
export const LANGUAGES: LanguageMeta[] = [
  { code: "en", name: "English", nativeName: "English", flag: "🇺🇸", dir: "ltr" },
  { code: "es", name: "Spanish", nativeName: "Español", flag: "🇪🇸", dir: "ltr" },
  { code: "fr", name: "French", nativeName: "Français", flag: "🇫🇷", dir: "ltr" },
  { code: "zh", name: "Chinese", nativeName: "中文", flag: "🇨🇳", dir: "ltr" },
  { code: "ar", name: "Arabic", nativeName: "العربية", flag: "🇸🇦", dir: "rtl" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी", flag: "🇮🇳", dir: "ltr" },
  { code: "pt", name: "Portuguese", nativeName: "Português", flag: "🇧🇷", dir: "ltr" },
  { code: "sw", name: "Swahili", nativeName: "Kiswahili", flag: "🇰🇪", dir: "ltr" },
];

const DICTS: Record<LangCode, Dict> = { en, es, fr, zh, ar, hi, pt, sw };

const STORAGE_KEY = "guardai-lang";

function readStoredLang(): LangCode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as LangCode | null;
    if (saved && DICTS[saved]) return saved;
    // Fall back to the browser's preferred language if we support it.
    const nav = (navigator.language || "en").slice(0, 2).toLowerCase() as LangCode;
    if (DICTS[nav]) return nav;
  } catch {
    /* ignore */
  }
  return "en";
}

export function langMeta(code: LangCode): LanguageMeta {
  return LANGUAGES.find((l) => l.code === code) || LANGUAGES[0];
}

// translate(): current locale → English → inline default → key.
export function translate(lang: LangCode, key: string, fallback?: string): string {
  const dict = DICTS[lang] as Record<string, string>;
  if (dict && dict[key] != null) return dict[key];
  const base = en as Record<string, string>;
  if (base[key] != null) return base[key];
  return fallback ?? key;
}

export interface LanguageContextValue {
  lang: LangCode;
  setLang: (code: LangCode) => void;
  dir: "ltr" | "rtl";
  languages: LanguageMeta[];
  meta: LanguageMeta;
  t: (key: TranslationKey | (string & {}), fallback?: string) => string;
}

export const LanguageContext = createContext<LanguageContextValue | null>(null);

export function useLanguageState(): LanguageContextValue {
  const [lang, setLangState] = useState<LangCode>(readStoredLang);

  const setLang = useCallback((code: LangCode) => {
    setLangState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* ignore */
    }
  }, []);

  const meta = langMeta(lang);
  const dir = meta.dir;

  // Reflect the active language on the document for RTL + a11y.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }, [lang, dir]);

  const t = useCallback(
    (key: string, fallback?: string) => translate(lang, key, fallback),
    [lang]
  );

  return useMemo(
    () => ({ lang, setLang, dir, languages: LANGUAGES, meta, t }),
    [lang, setLang, dir, meta, t]
  );
}

export function useTranslation(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useTranslation must be used within a LanguageProvider");
  return ctx;
}

// Wraps the app and supplies the language context.
export function LanguageProvider({ children }: { children: ReactNode }) {
  const value = useLanguageState();
  return createElement(LanguageContext.Provider, { value }, children);
}
